<template>
  <div>
    <!------------------
            HEADER
    -------------------->
    <div class="bg-white p-3 pr-4 pl-4 page-header" ref="pageHeader" style="padding-bottom: 0">
      <div class="row d-flex align-items-center">
        <div class="col">
          <div>
            <h4>
              <b-icon class="me-2" icon="file-earmark"></b-icon>
              Aufträge
            </h4>
          </div>
        </div>
        <div class="col-auto">
          <div class="form-inline">
            <!---------------------------------
                HEADER ACTION BUTTONS
            ---------------------------------->
            <b-button
              variant="light"
              class="mr-2"
              @click="$router.push('/orders/create')"
              v-if="$root.$user.isPlaner || $root.$user.isAdmin"
            >
              <b-icon icon="plus" variant="dark" class="mr-1"></b-icon>
              Auftrag
            </b-button>

            <!-- SEARCH FIELD  -->
            <b-input-group class="ml-2 header-search-field">
              <b-form-input
                type="search"
                size="sm"
                v-model.lazy="searchText"
                :class="{ 'bg-primary-light-3': searchText !== '' }"
              ></b-form-input>
              <template #prepend>
                <b-input-group-text>
                  <b-icon variant="dark" icon="search" />
                </b-input-group-text>
              </template>
            </b-input-group>
            <!-- FILTER BUTTON  :disabled="!isDeleteButtonEnabled"-->
            <b-button
              size="sm"
              :variant="filterToggle == true ? 'primary' : 'light'"
              class="ml-2"
              :pressed="filterToggle"
              @click="filterRecords"
              :disabled="selectedRows.length == 0 && !filterToggle"
            >
              <b-icon icon="funnel"></b-icon>
            </b-button>
            <!-- HELP ICON  -->
            <b-button size="sm" variant="light" class="ml-2" href="https://www.bauvisor.de/auftraege/" target="_blank">
              <b-icon icon="question-circle" variant="dark" class=""></b-icon>
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <div class="main-canvas">
      <div class="shadow-muted-super" ref="budgetSummarySection">
        <table class="w-100 bg-white">
          <tr class="border">
            <td class="px-2 py-1" colspan="8" v-b-toggle.collapse-orders @click="onWindowResize()">
              <small>Übersicht - Aktuelle Aufträge</small>
            </td>
            <td class="text-right pr-2">
              <b-button v-b-toggle.collapse-orders variant="white" size="sm" @click="onWindowResize()">
                <span class="when-opened">
                  <b-icon icon="chevron-up" class="action-button" />
                </span>
                <span class="when-closed"><b-icon icon="chevron-down" class="action-button" /></span>
              </b-button>
            </td>
          </tr>
        </table>

        <b-collapse id="collapse-orders" :visible="isTabOpen">
          <b-overlay :show="isHeaderLoading" rounded="sm" spinner-variant="primary" opacity="0.6">
            <table class="w-100 bg-white">
              <tr class="border border-top-0 border-bottom-0">
                <td class="p-2 font-weight-bold small border-bottom">VE-Budget</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudget, SettingsStore) }}
                  </div>
                </td>
                <td class="border-bottom">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-2 pl-4 font-weight-bold border-left small">Entfallende Leistungen</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVESkipped, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right border-bottom text-right pr-2 small">
                  <small>{{ $utils.numberWithMaxFrictions((budgetVESkipped / budgetVEBudget) * 100, 1) }}%</small>
                </td>
                <td colspan="6" class="border-right">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-2 pl-4 font-weight-bold border-left border-top border-bottom small">Offenes Budget</td>
                <td class="font-weight-bold border-bottom text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(
                        this.budgetVEBudgetOpen + this.budgetVEBudgetAusgeschrieben + this.budgetVEBudgetAusgewertet,
                        SettingsStore
                      )
                    }}
                  </div>
                </td>
                <td class="border-right border-bottom text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        ((this.budgetVEBudgetOpen +
                          this.budgetVEBudgetAusgeschrieben +
                          this.budgetVEBudgetAusgewertet) /
                          budgetVEBudget) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td colspan="6" class="border-bottom border-right">&nbsp;</td>
              </tr>

              <tr>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold border-left small">Beauftragtes Budget</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        ((this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced) / budgetVEBudget) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold small">Gesamtaufträge</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountSaldo, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        (this.budgetOrderAmountSaldo / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pt-2 pb-1 pl-4 font-weight-bold small">Nachtragsbudget</td>
                <td class="font-weight-bold text-right small">
                  <div class="bg-light m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(
                        this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo,
                        SettingsStore
                      )
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        ((this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small"><div class="m-0 py-1 px-2">&nbsp;</div></td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">Hauptaufträge</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderMainOrders, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        (this.budgetOrderMainOrders / this.budgetOrderAmountSaldo) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 pb-2 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small"><div class="m-0 py-1 px-2">&nbsp;</div></td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">Nachträge</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderOrderedAddendums, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        (this.budgetOrderOrderedAddendums / this.budgetOrderAmountSaldo) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 pb-2 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
              </tr>

              <tr>
                <td class="p-0 pl-5 border-left small">in Arbeit befindlich</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudgetInArbeit, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        (budgetVEBudgetInArbeit / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">in Arbeit befindlich</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountInArbeit, SettingsStore) }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{ $utils.numberWithMaxFrictions((budgetOrderAmountInArbeit / budgetOrderAmountSaldo) * 100, 1) }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">aktuell</td>
                <td class="p-0 text-right small">
                  <div class="m-0 py-1 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInArbeit - this.budgetOrderAmountInArbeit, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="p-0 border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        ((budgetVEBudgetInArbeit - budgetOrderAmountInArbeit) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">&nbsp;</td>
                <td class="p-0 text-right small"><div class="m-0 py-1 px-2">&nbsp;</div></td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pl-5 small">&nbsp;</td>
                <td class="p-0 text-right small">&nbsp;</td>
                <td class="p-0 border-right text-right pr-2 small">&nbsp;</td>
                <td class="py-0 pb-2 small" style="padding-left: 64px">Nachträge in Prüfung</td>
                <td class="p-0 text-right pr-2 pb-2 small">
                  {{ $utils.formatCurrency(this.budgetOrderCheckingAddendums, SettingsStore) }}
                </td>
                <td class="p-0 border-right text-right pr-2 pb-2 small"></td>
              </tr>
              <tr>
                <td class="p-0 pl-5 border-left small">schlussgerechnet</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{ $utils.formatCurrency(this.budgetVEBudgetInvoiced, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        (budgetVEBudgetInvoiced / (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced)) * 100,
                        1
                      )
                    }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">schlussgerechnet</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{ $utils.formatCurrency(this.budgetOrderAmountInvoiced, SettingsStore) }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{ $utils.numberWithMaxFrictions((budgetOrderAmountInvoiced / budgetOrderAmountSaldo) * 100, 1) }}%
                  </small>
                </td>
                <td class="p-0 pl-5 small">nicht beansprucht</td>
                <td class="text-right small">
                  <div class="m-0 py-0 px-2">
                    {{
                      $utils.formatCurrency(this.budgetVEBudgetInvoiced - this.budgetOrderAmountInvoiced, SettingsStore)
                    }}
                  </div>
                </td>
                <td class="border-right text-right pr-2 small">
                  <small>
                    {{
                      $utils.numberWithMaxFrictions(
                        ((this.budgetVEBudgetInvoiced - this.budgetOrderAmountInvoiced) /
                          (this.budgetVEBudgetInArbeit + this.budgetVEBudgetInvoiced - this.budgetOrderAmountSaldo)) *
                          100,
                        1
                      )
                    }}%
                  </small>
                </td>
              </tr>
              <tr>
                <td class="p-0 pl-5 pb-2 border-left small">&nbsp;</td>
                <td class="p-0 pb-2 text-right small"><div class="m-0 py-1 px-2">&nbsp;</div></td>
                <td class="p-0 pb-2 border-right text-right pr-2 small">&nbsp;</td>
                <td class="p-0 pb-2 pl-5 small">&nbsp;</td>
                <td class="p-0 pb-2 text-right small">&nbsp;</td>
                <td class="p-0 pb-2 border-right text-right pr-2 small">&nbsp;</td>
                <td class="py-0 pb-2 small" style="padding-left: 64px">Nachträge in Prüfung</td>
                <td class="p-0 pb-2 text-right pr-2 small">
                  {{ $utils.formatCurrency(this.budgetOrderCheckingAddendumsFinal, SettingsStore) }}
                </td>
                <td class="p-0 pb-2 border-right text-right pr-2 small"></td>
              </tr>
            </table>
          </b-overlay>
        </b-collapse>
      </div>
      <div class="table-wrapper shadow-muted-super mt-3">
        <b-overlay :show="isLoading" rounded="sm" spinner-variant="primary" opacity="0.6">
          <b-table
            @row-selected="onRowSelected"
            ref="theTable"
            selectable
            :items="items"
            :fields="visibleFields"
            class="bg-white sum-header mb-0"
            head-variant="light"
            no-border-collapse
            small
            :sticky-header="tableHeight"
            :filter="searchText"
            show-empty
            @filtered="items => recalcSums(items)"
          >
            <template #head(checked)>
              <b-form-checkbox
                :disabled="items.length == 0"
                v-model="checkedAllTableEntries"
                @change="onCheckAllTableEntries"
              ></b-form-checkbox>
            </template>
            <template #head(BudgetSaldo)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(budgetSaldoTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(Amount)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(amountTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountAddendumOrdered)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(nachtragSummeTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(NachtragsBudget)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(nachtragsBudgetTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountSaldo)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(totalAmountTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(Invoiced)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(invoicedTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(ObligoAmount)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(obligoTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(AmountAddendumChecking)="data">
              <div style="padding-right: 2px">
                {{ data.label }}
              </div>
              <div
                class="font-weight-bold text-right text-grey text-nowrap"
                :class="{ 'text-primary': searchText != '' || filterToggle }"
              >
                <b-icon icon="funnel-fill" v-if="searchText != '' || filterToggle" />
                {{ $utils.formatCurrency(checkingTotal, SettingsStore) }}
              </div>
            </template>
            <template #head(actions)="data">
              {{ data.label }}

              <span @click="$bvModal.show('orders')" role="button">
                <b-icon icon="three-dots-vertical" class="float-right no-outline" style="margin-top: 4px" />
              </span>
              <!--
              <b-modal
                cancel-disabled
                ok-disabled
                hide-footer
                hide-backdrop
                hide-header
                no-fade
                id="column-selector-orders-modal"
                body-class="p-0"
                content-class="border-0"
              >
                <b-list-group>
                  <b-list-group-item v-for="item in fields.filter(x => x.hideable == true)" :key="item.key" class="p-2">
                    <b-form-checkbox
                      @change="onClickColumnVisible(item)"
                      v-model="item.visible"
                      value="true"
                      unchecked-value="false"
                    >
                      {{ item.label }}
                    </b-form-checkbox>
                  </b-list-group-item>
                </b-list-group>
              </b-modal>-->
            </template>
            <template #cell(actions)="data">
              <b-icon
                class="action-button text-grey"
                style="width: 18px; height: 18px"
                icon="arrow-right"
                title="Auftrag öffnen"
                @click.stop="navigateTo(data.item)"
              />
              <b-icon
                class="action-button text-grey ml-2"
                style="width: 18px; height: 18px"
                icon="card-list"
                title="Nachträge"
                @click.stop="navigateTo(data.item, true)"
              />

              <b-icon
                icon="trash"
                variant="grey"
                class="action-button ml-2"
                style="width: 16px; height: 16px"
                title="Auftrag löschen"
                @click.stop="deleteRecord(data.item)"
                v-if="$root.$user.isPlaner || $root.$user.isAdmin"
              />
            </template>
            <template #cell(checked)="data">
              <b-form-checkbox
                @change="checked(data)"
                v-model="data.rowSelected"
                :disabled="data.item.editMode"
              ></b-form-checkbox>
            </template>
            <template #cell(Ordinal)="data">
              <div @click.stop="navigateTo(data.item, true)">{{ data.item.Ordinal }}</div>
            </template>
            <template #cell(DisplayName)="data">
              <div
                @click.stop="navigateTo(data.item, true)"
                style="width: 175px; text-overflow: ellipsis; overflow: hidden"
              >
                {{ data.item.DisplayName }}
              </div>
            </template>
            <template #cell(BudgetSaldo)="data">
              <div @click.stop="navigateToVEBudgeting(data.item)">
                {{ $utils.formatCurrency(data.item.BudgetSaldo, SettingsStore) }}
              </div>
            </template>
            <template #cell(ProcessStatus)="data">
              <div @click.stop="navigateTo(data.item)">
                {{ data.item.StatusText == 'offen' ? data.item.ProcessStatus : data.item.StatusText }}
              </div>
            </template>
            <template #cell(KGR)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.item.KGR || '&nbsp;' }}</div>
            </template>
            <template #cell(Responsible)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.item.Responsible || '&nbsp;' }}</div>
            </template>
            <template #cell(FA1)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.item.FA1 || '&nbsp;' }}</div>
            </template>
            <template #cell(FA2)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.item.FA2 || '&nbsp;' }}</div>
            </template>
            <template #cell(SupplierText)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.item.SupplierText }}</div>
            </template>
            <template #cell(OrderStatus)="data">
              <div @click.stop="navigateTo(data.item)">{{ data.value }}</div>
            </template>

            <template #cell(Amount)="data">
              <div @click.stop="navigateTo(data.item)">
                {{ $utils.formatCurrency(data.item.Amount, SettingsStore) }}
              </div>
            </template>
            <template #cell(AmountAddendumOrdered)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{ $utils.formatCurrency(data.item.AmountAddendumOrdered, SettingsStore) }}
              </div>
            </template>
            <template #cell(TotalAmount)="data">
              <div @click.stop="navigateTo(data.item, true)">
                {{
                  $utils.formatCurrency(
                    parseFloat(data.item.Amount) + parseFloat(data.item.AmountAddendumOrdered),
                    SettingsStore
                  )
                }}
              </div>
            </template>
            <template #cell(Invoiced)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{ $utils.formatCurrency(data.item.Invoiced, SettingsStore) }}
              </div>
            </template>
            <template #cell(InvoicedPercent)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{
                  $utils.numberWithMaxFrictions(
                    (parseFloat(data.item.Invoiced) /
                      (parseFloat(data.item.Amount) + parseFloat(data.item.AmountAddendumOrdered))) *
                      100,
                    1
                  )
                }}
              </div>
            </template>
            <template #cell(AmountSaldo)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.AmountSaldo, SettingsStore) }}
              </div>
            </template>
            <template #cell(NachtragsBudget)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.BudgetSaldo - data.item.AmountSaldo, SettingsStore) }}
              </div>
            </template>

            <template #cell(AmountAddendumChecking)="data">
              <div @click.stop="$router.push('/orders/' + data.item.DbId + '/addendums')">
                {{ $utils.formatCurrency(data.item.AmountAddendumChecking, SettingsStore) }}
              </div>
            </template>
            <template #cell(ObligoAmount)="data">
              <div @click.stop="navigateToInvoices(data.item)">
                {{ $utils.formatCurrency(data.item.ObligoAmount, SettingsStore) }}
              </div>
            </template>
            <template #cell(HasFinalInvoice)="data">
              <!-- schlussgerechnet UND geprüft -->
              <b-badge
                pill
                class="sr-kennzeichen"
                v-if="data.item.HasFinalInvoice > 0 && data.item.FinalAmountStatus == 2"
                variant="info"
                style="font-size: 100%"
                title="schlussgerechnet, geprüft"
              >
                <b-icon class="p-0" icon="check-2" />
              </b-badge>
              <!-- schlussgerechnet, aber nicht geprüft -->
              <b-badge
                pill
                class="sr-kennzeichen light"
                v-if="data.item.HasFinalInvoice > 0 && data.item.FinalAmountStatus != 2"
                variant="info"
                style="font-size: 100%"
                title="schlussgerechnet, ungeprüft"
              >
                <b-icon class="p-0" icon="check-2" />
              </b-badge>
            </template>
          </b-table>
        </b-overlay>
      </div>
    </div>
    <TableColumnSelector
      :fields="fields"
      id="orders"
      @ok="AppState.updateVisibleFields('orders', fields)"
    ></TableColumnSelector>
  </div>
</template>

<script>
import AppState from '@/store/App.js';
import OrdersService from '@/service/OrdersService.js';
import SettingsStore from '@/store/Settings';
import BudgetService from '@/service/BudgetService';
import TableColumnSelector from '@/components/TableColumnSelector';

export default {
  components: {
    TableColumnSelector
  },
  data() {
    return {
      AppState,
      SettingsStore,
      isLoading: true,
      isHeaderLoading: true,
      tableHeight: '500px',
      checkedAllTableEntries: false,
      filterToggle: false,
      selectedRows: [],
      items: [],
      budgetKKE: 0.0,
      budgetPAT: 0.0,
      budgetVEBudget: 0.0,
      budgetVESkipped: 0.0,
      budgetVEBudgetOpen: 0.0,
      budgetVEBudgetInvoiced: 0.0,
      budgetVEBudgetAusgeschrieben: 0.0,
      budgetVEBudgetAusgewertet: 0.0,
      budgetVEBudgetInArbeit: 0.0,
      budgetOrderAmountSaldo: 0.0,
      budgetOrderAmountInvoiced: 0.0,
      budgetOrderAmountInArbeit: 0.0,
      budgetOrderAmountLeistungInvoiced: 0.0,
      budgetOrderAmountLeistungInvoicedFinal: 0.0,
      budgetOrderAmountLeistungInvoicedPartly: 0.0,
      budgetOrderMainOrders: 0.0,
      budgetOrderOrderedAddendums: 0.0,
      budgetOrderCheckingAddendums: 0.0,
      budgetOrderCheckingAddendumsFinal: 0.0,
      fields: [
        {
          hideable: false,
          visible: true,
          key: 'checked',
          label: '',
          tdClass: 'sticky-col first-col text-center align-middle',
          thClass: 'sticky-col first-col text-center align-top noselect',
          thStyle: 'z-index:10;'
        },
        {
          hideable: false,
          visible: true,
          key: 'Ordinal',
          label: 'VE',
          thClass: 'align-top sticky-col second-col',
          tdClass: 'sticky-col second-col',
          sortable: true,
          thStyle: 'z-index:10;'
        },
        {
          visible: true,
          hideable: false,
          key: 'DisplayName',
          label: 'Bezeichnung',
          thClass: 'align-top sticky-col third-col',
          tdClass: 'sticky-col third-col',
          sortable: true,
          thStyle: 'z-index:10;'
        },
        {
          hideable: SettingsStore.settings['KGR_Selections_Active'] == 'true',
          visible: SettingsStore.settings['KGR_Selections_Active'] == 'true',
          key: 'KGR',
          label: 'Kostengruppe',
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'Responsible',
          label: 'Verantwortlich',
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'FA1',
          label: SettingsStore.settings['FA1'],
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'FA2',
          label: SettingsStore.settings['FA2'],
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'ProcessStatus',
          label: 'Status',
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'SupplierText',
          label: 'Auftragnehmer',
          tdClass: 'bg-light',
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: true,
          visible: true,
          key: 'OrderStatus',
          label: 'Auftragsstatus / Datum',
          tdClass: 'bg-light',
          sortable: true,
          thClass: 'align-top',
          formatter: (value, key, item) => {
            if (item.veSection && item.veSection == 'A') {
              return (
                (item.VergabeEmpfehlung && item.VergabeEmpfehlung != '' ? item.VergabeEmpfehlung : 'Empfehlung') +
                ' / ' +
                this.$utils.SQLDateToGermanDate(item.VergabeEmpfehlungDatum)
              );
            } else if (item.veSection && item.veSection == 'B') {
              return (
                (item.OrderNumber && item.OrderNumber != '' ? item.OrderNumber : 'Bestellung') +
                ' / ' +
                this.$utils.SQLDateToGermanDate(item.OrderDate)
              );
            } else return '';
          },
          sortByFormatted: true
        },
        {
          hideable: true,
          visible: true,
          key: 'BudgetSaldo',
          label: 'Budget',
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'text-right',
          thStyle: 'padding-right: 12px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'Amount',
          label: 'Hauptauftrag',
          tdClass: 'bg-light text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'padding-right: 12px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountAddendumOrdered',
          label: 'Nachträge',
          tdClass: 'bg-light text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'padding-right: 12px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountSaldo',
          label: 'Gesamtauftrag',
          tdClass: 'bg-light text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'padding-right: 12px;',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore)
        },
        {
          hideable: true,
          visible: true,
          key: 'NachtragsBudget',
          label: 'Nachtragsbudget',
          tdClass: 'bg-light text-right',
          sortable: true,
          thClass: 'align-top text-right',
          thStyle: 'padding-right: 12px;',
          formatter: (k, v, item) => {
            return this.$utils.formatCurrency(item.BudgetSaldo - item.AmountSaldo, SettingsStore);
          }
        },
        {
          hideable: true,
          visible: true,
          key: 'AmountAddendumChecking',
          label: 'Offene Nachträge',
          tdClass: 'bg-light text-right',
          sortable: true,
          thClass: 'align-top text-right text-wrap',
          thStyle: 'padding-right: 12px;',
          formatter: x => this.$utils.formatCurrency(x, SettingsStore)
        },
        {
          hideable: true,
          visible: true,
          key: 'Invoiced',
          label: 'Abgerechnet',
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'text-right',
          thStyle: 'padding-right: 12px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'InvoicedPercent',
          label: '%',
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'text-right'
        },
        {
          hideable: true,
          visible: true,
          key: 'ObligoAmount',
          label: 'Obligo',
          sortable: true,
          thClass: 'align-top text-right',
          tdClass: 'text-right',
          thStyle: 'padding-right: 12px;'
        },
        {
          hideable: true,
          visible: true,
          key: 'HasFinalInvoice',
          label: 'SR',
          sortable: true,
          thClass: 'align-top',
          tdClass: 'text-center'
        },
        {
          hideable: true,
          visible: true,
          key: 'AbnahmeDatum',
          label: 'Abnahme',
          formatter: x => this.$utils.SQLDateToGermanDate(x),
          sortable: true,
          thClass: 'align-top'
        },
        {
          hideable: false,
          visible: true,
          key: 'actions',
          label: 'Aktionen',
          tdClass: 'sticky-col last-col text-center align-middle',
          thClass: 'sticky-col last-col align-top noselect',
          thStyle: 'z-index: 10;'
        }
      ],
      budgetSaldoTotal: 0,
      amountTotal: 0,
      nachtragSummeTotal: 0,
      totalAmountTotal: 0,
      nachtragsBudgetTotal: 0,
      invoicedTotal: 0,
      obligoTotal: 0,
      checkingTotal: 0,
      searchText: ''
    };
  },
  methods: {
    recalcSums(filteredItems) {
      this.budgetSaldoTotal = 0;
      this.amountTotal = 0;
      this.nachtragSummeTotal = 0;
      this.totalAmountTotal = 0;
      this.nachtragsBudgetTotal = 0;
      this.invoicedTotal = 0;
      this.checkingTotal = 0;
      this.obligoTotal = 0;
      filteredItems.map(x => {
        this.budgetSaldoTotal += parseFloat(x.BudgetSaldo);
        this.amountTotal += parseFloat(x.Amount);
        this.nachtragSummeTotal += parseFloat(x.AmountAddendumOrdered);
        this.totalAmountTotal += parseFloat(x.AmountSaldo);
        this.nachtragsBudgetTotal += parseFloat(x.BudgetSaldo) - parseFloat(x.AmountSaldo);
        this.invoicedTotal += parseFloat(x.Invoiced);
        this.checkingTotal += x.AmountAddendumChecking ? parseFloat(x.AmountAddendumChecking) : 0;
        if (x.HasFinalInvoice != 0 && x.FinalAmountChecked != 0) {
          x.ObligoAmount = 0;
        } else {
          x.ObligoAmount = parseFloat(x.Amount) + parseFloat(x.AmountAddendumOrdered) - parseFloat(x.Invoiced);
          this.obligoTotal += x.ObligoAmount;
        }

        x.checked = false;
        return x;
      });
    },
    onClickColumnVisible() {
      AppState.updateVisibleFields('orders', this.fields);
    },
    onWindowResize() {
      setTimeout(() => {
        const totalHeight = window.innerHeight;
        const budgetSummarySection = this.$refs.budgetSummarySection;
        //const headerHeight = this.$refs.pageHeader.offsetHeight;
        const tableHeight = totalHeight - budgetSummarySection.offsetHeight - 120;
        this.tableHeight = tableHeight + 'px';
      }, 300);
    },
    navigateTo(item, nachtrag = false) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/orders/' + item.DbId + (nachtrag ? '/addendums' : '/view'));
    },
    navigateToVEBudgeting(item) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/allocationunits/' + item.VeDbId);
    },
    navigateToInvoices(item) {
      if (item.editMode) return; // cancel navigation, if item is in edit mode
      this.$router.push('/invoices/' + item.DbId);
    },
    deleteRecord(item) {
      var questionText = '';

      OrdersService.maybeDeleted(item.DbId)
        .then(() => {
          if (item.ProcessStatus == 'empfohlen') {
            questionText = `Möchten Sie die Vergabeempfehlung wirklich zurückziehen?`;
          } else if (item.ProcessStatus == 'beauftragt') {
            questionText = `Möchten Sie den Auftrag wirklich löschen?<br><br>Bitte klären Sie ggf. die Konsequenzen einer Vertragskündigung.`;
          }

          const messageVNode = this.$createElement('div', { domProps: { innerHTML: questionText } });
          this.$bvModal
            .msgBoxConfirm([messageVNode], {
              title: 'Auftrag löschen',
              okTitle: 'Bestätigen',
              cancelTitle: 'Abbrechen',
              okVariant: 'danger',

              centered: true
            })
            .then(confirmed => {
              if (confirmed) {
                this.isLoading = true;
                OrdersService.delete(item)
                  .then(() => {
                    this.$utils.deleteItemFromArray(this.items, x => x.DbId == item.DbId);
                    this.$showSuccess(this, 'Auftrag löschen', 'Auftrag erfolgreich gelöscht');
                  })
                  .catch(err => {
                    if (err.response) this.$showError(this, err.response.data);
                    else this.$showError(this, err.toString());
                  })
                  .finally(() => (this.isLoading = false));
              }
            });
        })
        .catch(err => {
          console.log(err);
          questionText = 'Der Auftrag kann aus folgendem Grund nicht gelöscht werden:<br><br>';
          if (err.hasAddendums) questionText += '<li class="ml-3">Nachträge vorhanden</li>';
          if (err.hasInvoices) questionText += '<li class="ml-3">Belege vorhanden</li>';
          questionText += '<br>Bitte löschen Sie zunächst diese Objekte, bevor der Auftrag gelöscht werden kann.';

          const messageVNode = this.$createElement('div', { domProps: { innerHTML: questionText } });

          this.$bvModal.msgBoxOk([messageVNode], {
            title: 'Auftrag löschen',
            okTitle: 'OK',
            okVariant: 'danger',

            centered: true
          });
        });
    },
    onCheckAllTableEntries() {
      if (this.checkedAllTableEntries) {
        this.$refs.theTable.selectAllRows();
      } else {
        this.$refs.theTable.clearSelected();
      }
    },
    onRowSelected(selectedItems) {
      this.selectedRows = selectedItems;
      if (selectedItems.length < this.items.length) {
        this.checkedAllTableEntries = false;
      } else {
        this.checkedAllTableEntries = true;
      }
    },
    checked(data) {
      if (data.rowSelected) {
        this.$refs.theTable.selectRow(data.index);
      } else {
        this.$refs.theTable.unselectRow(data.index);
      }
    },
    filterRecords() {
      if (!this.filterToggle) {
        this.filterToggle = true;
        this.itemsBackup = [...this.items];
        this.items = this.selectedRows;
        this.$nextTick(() => {
          this.$refs.theTable.selectAllRows();
        });
      } else {
        this.filterToggle = false;
        this.items = this.itemsBackup;
        this.selectedRows.forEach(row => {
          this.$nextTick(() => this.$refs.theTable.selectRow(this.items.findIndex(item => row.DbId == item.DbId)));
        });
      }
      this.recalcSums(this.items);
    }
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible == true || field.visible == 'true');
    },
    isTabOpen() {
      return localStorage.getItem('collapse-orders') == 'true' ? true : false;
    }
  },
  created() {
    this.$eventBus.$on('projectSwitched', () => {
      this.searchText = '';
    });
  },
  activated() {
    console.log('zzz Orders.vue');
    AppState.loadVisibleFields('orders', this.fields);
    window.addEventListener('resize', this.onWindowResize); // listen for windows size changes, to re-compute height of controls

    this.fields.find(x => x.key == 'KGR').hideable = SettingsStore.settings['KGR_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'Responsible').hideable =
      SettingsStore.settings['Responsible_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'FA1').hideable = SettingsStore.settings['FA1_Active'] == 'true';
    this.fields.find(x => x.key == 'FA2').hideable = SettingsStore.settings['FA2_Active'] == 'true';

    this.fields.find(x => x.key == 'KGR').visible =
      this.fields.find(x => x.key == 'KGR').visible && SettingsStore.settings['KGR_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'Responsible').visible =
      this.fields.find(x => x.key == 'Responsible').visible &&
      SettingsStore.settings['Responsible_Selections_Active'] == 'true';
    this.fields.find(x => x.key == 'FA1').visible =
      this.fields.find(x => x.key == 'FA1').visible && SettingsStore.settings['FA1_Active'] == 'true';
    this.fields.find(x => x.key == 'FA2').visible =
      this.fields.find(x => x.key == 'FA2').visible && SettingsStore.settings['FA2_Active'] == 'true';

    this.fields.find(x => x.key == 'FA1').label = SettingsStore.settings['FA1'];
    this.fields.find(x => x.key == 'FA2').label = SettingsStore.settings['FA2'];

    this.onWindowResize();

    this.isLoading = true;
    this.isHeaderLoading = true;
    AppState.isLoading = false;
    BudgetService.getSummary()
      .then(summary => {
        this.budgetKKE = summary.KkeBudgetSummary;
        this.budgetPAT = summary.PatBudgetSummary;

        this.budgetVESkipped = summary.VEBudgetSkipped;
        this.budgetVEBudgetOpen = summary.VEBudgetOpen;
        this.budgetVEBudgetInvoiced = summary.VEBudgetInvoiced;
        this.budgetVEBudgetAusgeschrieben = summary.VEBudgetAusgeschrieben;
        this.budgetVEBudgetAusgewertet = summary.VEBudgetAusgewertet;
        this.budgetVEBudgetInArbeit = summary.VEBudgetInArbeit;
        this.budgetOrderAmountSaldo = summary.OrderAmountSaldo;
        this.budgetOrderAmountInvoiced = summary.OrderAmountInvoiced;
        this.budgetOrderAmountInArbeit = summary.OrderAmountInArbeit;
        this.budgetOrderAmountLeistungInvoiced = summary.OrderAmountLeistungInvoiced;
        this.budgetOrderAmountLeistungInvoicedFinal = summary.OrderAmountLeistungInvoicedFinal;
        this.budgetOrderAmountLeistungInvoicedPartly = summary.OrderAmountLeistungInvoicedPartly;
        this.budgetVEBudget =
          this.budgetVESkipped +
          this.budgetVEBudgetOpen +
          this.budgetVEBudgetAusgeschrieben +
          this.budgetVEBudgetAusgewertet +
          this.budgetVEBudgetInArbeit +
          this.budgetVEBudgetInvoiced;
        this.budgetOrderMainOrders = summary.OrderMainOrders;
        this.budgetOrderOrderedAddendums = summary.OrderOrderedAddendums;
        this.budgetOrderCheckingAddendums = summary.OrderCheckingAddendums;
        this.budgetOrderCheckingAddendumsFinal = summary.OrderCheckingAddendumsFinal;
      })
      .catch(err => console.log(err.toString()))
      .finally(() => {
        this.isHeaderLoading = false;
      });
    OrdersService.get()
      .then(items => {
        this.budgetSaldoTotal = 0;
        this.amountTotal = 0;
        this.nachtragSummeTotal = 0;
        this.totalAmountTotal = 0;
        this.nachtragsBudgetTotal = 0;
        this.invoicedTotal = 0;
        this.checkingTotal = 0;
        this.obligoTotal = 0;
        this.items = items.map(x => {
          this.budgetSaldoTotal += parseFloat(x.BudgetSaldo);
          this.amountTotal += parseFloat(x.Amount);
          this.nachtragSummeTotal += parseFloat(x.AmountAddendumOrdered);
          this.totalAmountTotal += parseFloat(x.AmountSaldo);
          this.nachtragsBudgetTotal += parseFloat(x.BudgetSaldo) - parseFloat(x.AmountSaldo);
          this.invoicedTotal += parseFloat(x.Invoiced);
          this.checkingTotal += x.AmountAddendumChecking ? parseFloat(x.AmountAddendumChecking) : 0;
          if (x.HasFinalInvoice != 0 && x.FinalAmountChecked != 0) {
            x.ObligoAmount = 0;
          } else {
            x.ObligoAmount = parseFloat(x.Amount) + parseFloat(x.AmountAddendumOrdered) - parseFloat(x.Invoiced);
            this.obligoTotal += x.ObligoAmount;
          }

          x.checked = false;
          return x;
        });
        this.selectedRows.forEach(row => {
          this.$nextTick(() => this.$refs.theTable.selectRow(this.items.findIndex(item => row.DbId == item.DbId)));
        });

        if (this.filterToggle) {
          this.itemsBackup = [...this.items];
          this.items = this.selectedRows;
          this.$nextTick(() => {
            this.$refs.theTable.selectAllRows();
          });
        }
        this.isLoading = false;
      })
      .finally(() => {
        if (AppState.scrollPosition[this.$route.path])
          this.$refs.theTable.$el.scrollTo({
            top: AppState.scrollPosition[this.$route.path][0],
            left: AppState.scrollPosition[this.$route.path][1]
          });
      });
  },

  deactivated() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  beforeRouteLeave(to, from, next) {
    AppState.scrollPosition[from.path] = [this.$refs.theTable.$el.scrollTop, this.$refs.theTable.$el.scrollLeft];
    next();
  }
};
</script>

<style>
.page-header {
  border-bottom: 1px solid #dee2e6;
}
.table-wrapper {
  overflow: auto;
  white-space: nowrap;
  border: 1px solid #dee2e6;
}
.first-col {
  width: 50px;
  min-width: 50px;
  max-width: 50px;
  left: 0;
}
.second-col {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  left: 50px;
}

.third-col {
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  left: 150px;
  text-overflow: ellipsis;
  overflow: hidden;
}
.last-col {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  right: 0;
}
.table-wrapper tbody tr td.sticky-col {
  background-color: var(--bauvisor-primary-light) !important;
}
.table-striped tbody tr:nth-of-type(odd) td.sticky-col {
  background-color: #efefef !important;
}
.table-striped tbody tr:nth-of-type(even) td.sticky-col {
  background-color: #f5f5f5 !important;
}
.table-wrapper td:not(:last-child) {
  border-right: 1px solid #dee2e6;
}
.table-wrapper td:last-child {
  border-left: 1px solid #ced2d6;
}
.table-wrapper tr:last-child td {
  border-bottom: 1px solid #ced2d6;
}

.table-wrapper td:nth-last-child(2) {
  border-left: none;
  border-right: none;
}
.table.b-table > tbody > .table-active,
.table.b-table > tbody > .table-active > th,
.table.b-table > tbody > .table-active > td {
  background-color: #f5f5f5;
}

#column-selector-orders-modal > .modal-dialog {
  position: absolute;
  top: 100px;
  right: 13px;
}
.sr-kennzeichen {
  display: inline;
  vertical-align: middle;
  border-radius: 4px;
  padding-left: 3px;
  padding-right: 3px;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
